/**
 * TQL Parser - Tokenizer and Parser for HTTP Query Language
 */

import type {
  TqlAstNode,
  TqlFieldExpression,
  TqlHeaderExpression,
  TqlPresetReference,
  TqlLogicalExpression,
  TqlNotExpression,
  TqlGroupExpression,
  TqlParseResult,
  TqlField,
  StringOperator,
  NumericOperator,
  ExistenceOperator,
} from './types';

// Token types
type TokenType =
  | 'FIELD'
  | 'OPERATOR'
  | 'STRING'
  | 'NUMBER'
  | 'LPAREN'
  | 'RPAREN'
  | 'DOT'
  | 'AND'
  | 'OR'
  | 'NOT'
  | 'PRESET'
  | 'COLON'
  | 'EOF';

type Token = {
  type: TokenType;
  value: string;
  position: number;
};

const PARSE_CACHE_MAX_SIZE = 100;
const parseResultCache = new Map<string, TqlParseResult>();

// Valid field names
const VALID_FIELDS = new Set([
  'req.method',
  'req.path',
  'req.query',
  'req.host',
  'req.body',
  'req.raw',
  'req.ext',
  'req.port',
  'req.header',
  'resp.code',
  'resp.body',
  'resp.raw',
  'resp.roundtrip',
  'resp.length',
  'resp.mime',
  'resp.header',
  'id',
  'timestamp',
  'comment',
  'tag',
  'highlight',
]);

// Valid operators
const VALID_OPERATORS = new Set([
  'eq',
  'neq',
  'cont',
  'ncont',
  'starts',
  'ends',
  'regex',
  'gt',
  'gte',
  'lt',
  'lte',
  'exists',
  'nexists',
]);

// Tokenizer
class Tokenizer {
  private input: string;
  private position = 0;
  private tokens: Token[] = [];

  constructor(input: string) {
    this.input = input;
  }

  tokenize(): Token[] {
    this.tokens = [];
    this.position = 0;

    while (this.position < this.input.length) {
      this.skipWhitespace();
      if (this.position >= this.input.length) break;

      const char = this.input[this.position];

      // String literals
      if (char === '"' || char === "'") {
        this.tokens.push(this.readString(char));
        continue;
      }

      // Parentheses
      if (char === '(') {
        this.tokens.push({
          type: 'LPAREN',
          value: '(',
          position: this.position,
        });
        this.position++;
        continue;
      }

      if (char === ')') {
        this.tokens.push({
          type: 'RPAREN',
          value: ')',
          position: this.position,
        });
        this.position++;
        continue;
      }

      // Dot
      if (char === '.') {
        this.tokens.push({ type: 'DOT', value: '.', position: this.position });
        this.position++;
        continue;
      }

      // Colon (for preset syntax)
      if (char === ':') {
        this.tokens.push({
          type: 'COLON',
          value: ':',
          position: this.position,
        });
        this.position++;
        continue;
      }

      // Numbers
      if (/\d/.test(char)) {
        this.tokens.push(this.readNumber());
        continue;
      }

      // Identifiers (fields, operators, keywords)
      if (/[a-zA-Z_-]/.test(char)) {
        this.tokens.push(this.readIdentifier());
        continue;
      }

      // Unknown character
      throw new Error(
        `Unexpected character '${char}' at position ${this.position}`,
      );
    }

    this.tokens.push({ type: 'EOF', value: '', position: this.position });
    return this.tokens;
  }

  private skipWhitespace(): void {
    while (
      this.position < this.input.length &&
      /\s/.test(this.input[this.position])
    ) {
      this.position++;
    }
  }

  private readString(quote: string): Token {
    const start = this.position;
    this.position++; // Skip opening quote
    let value = '';

    while (this.position < this.input.length) {
      const char = this.input[this.position];

      if (char === quote) {
        this.position++; // Skip closing quote
        return { type: 'STRING', value, position: start };
      }

      if (char === '\\' && this.position + 1 < this.input.length) {
        // Handle escape sequences
        this.position++;
        const escaped = this.input[this.position];
        switch (escaped) {
          case 'n':
            value += '\n';
            break;
          case 't':
            value += '\t';
            break;
          case 'r':
            value += '\r';
            break;
          case '\\':
            value += '\\';
            break;
          case '"':
            value += '"';
            break;
          case "'":
            value += "'";
            break;
          default:
            value += escaped;
        }
        this.position++;
        continue;
      }

      value += char;
      this.position++;
    }

    throw new Error(`Unterminated string starting at position ${start}`);
  }

  private readNumber(): Token {
    const start = this.position;
    let value = '';

    while (
      this.position < this.input.length &&
      /\d/.test(this.input[this.position])
    ) {
      value += this.input[this.position];
      this.position++;
    }

    return { type: 'NUMBER', value, position: start };
  }

  private readIdentifier(): Token {
    const start = this.position;
    let value = '';

    while (
      this.position < this.input.length &&
      /[a-zA-Z0-9_-]/.test(this.input[this.position])
    ) {
      value += this.input[this.position];
      this.position++;
    }

    const upperValue = value.toUpperCase();

    // Check for keywords
    if (upperValue === 'AND') {
      return { type: 'AND', value: 'AND', position: start };
    }
    if (upperValue === 'OR') {
      return { type: 'OR', value: 'OR', position: start };
    }
    if (upperValue === 'NOT') {
      return { type: 'NOT', value: 'NOT', position: start };
    }
    if (value === 'preset') {
      return { type: 'PRESET', value: 'preset', position: start };
    }

    // Check if it's an operator
    if (VALID_OPERATORS.has(value)) {
      return { type: 'OPERATOR', value, position: start };
    }

    // Otherwise it's a field or partial field
    return { type: 'FIELD', value, position: start };
  }
}

// Parser
class Parser {
  private tokens: Token[] = [];
  private current = 0;

  parse(input: string): TqlParseResult {
    try {
      const tokenizer = new Tokenizer(input);
      this.tokens = tokenizer.tokenize();
      this.current = 0;

      if (this.isAtEnd()) {
        return { success: true, ast: this.createEmptyExpression() };
      }

      const ast = this.parseExpression();

      if (!this.isAtEnd()) {
        return {
          success: false,
          error: `Unexpected token: ${this.peek().value}`,
          position: this.peek().position,
        };
      }

      return { success: true, ast };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown parse error',
      };
    }
  }

  private createEmptyExpression(): TqlAstNode {
    // Return a "match all" expression
    return {
      type: 'field',
      field: 'req.path',
      operator: 'cont',
      value: '',
    };
  }

  private parseExpression(): TqlAstNode {
    return this.parseOr();
  }

  private parseOr(): TqlAstNode {
    let left = this.parseAnd();

    while (this.check('OR')) {
      this.advance();
      const right = this.parseAnd();
      left = {
        type: 'logical',
        operator: 'OR',
        left,
        right,
      } satisfies TqlLogicalExpression;
    }

    return left;
  }

  private parseAnd(): TqlAstNode {
    let left = this.parseUnary();

    while (this.check('AND')) {
      this.advance();
      const right = this.parseUnary();
      left = {
        type: 'logical',
        operator: 'AND',
        left,
        right,
      } satisfies TqlLogicalExpression;
    }

    return left;
  }

  private parseUnary(): TqlAstNode {
    if (this.check('NOT')) {
      this.advance();
      const expression = this.parseUnary();
      return {
        type: 'not',
        expression,
      } satisfies TqlNotExpression;
    }

    return this.parsePrimary();
  }

  private parsePrimary(): TqlAstNode {
    // Grouped expression
    if (this.check('LPAREN')) {
      this.advance();
      const expression = this.parseExpression();
      this.consume('RPAREN', 'Expected closing parenthesis');
      return {
        type: 'group',
        expression,
      } satisfies TqlGroupExpression;
    }

    // Preset reference: preset:name
    if (this.check('PRESET')) {
      this.advance();
      this.consume('COLON', 'Expected ":" after "preset"');
      const nameToken = this.consume('FIELD', 'Expected preset name');
      return {
        type: 'preset',
        presetName: nameToken.value,
      } satisfies TqlPresetReference;
    }

    // Field expression
    return this.parseFieldExpression();
  }

  private parseFieldExpression(): TqlAstNode {
    // Parse field name (may be compound like req.path or req.header)
    const fieldParts: string[] = [];
    const firstToken = this.consume('FIELD', 'Expected field name');
    fieldParts.push(firstToken.value);

    // Read dotted parts
    while (this.check('DOT') && !this.isAtEnd()) {
      this.advance(); // consume dot
      const partToken = this.advance();
      if (partToken.type !== 'FIELD' && partToken.type !== 'OPERATOR') {
        throw new Error(`Expected field part after dot, got ${partToken.type}`);
      }
      fieldParts.push(partToken.value);
    }

    const fieldName = fieldParts.join('.');

    // Check for header access pattern: req.header["Name"] or resp.header["Name"]
    if (fieldName === 'req.header' || fieldName === 'resp.header') {
      return this.parseHeaderExpression(
        fieldName === 'req.header' ? 'req' : 'resp',
      );
    }

    // Validate field name
    const baseField = fieldParts.slice(0, -1).join('.');
    const operatorName = fieldParts[fieldParts.length - 1];

    // Check if this is field.operator pattern
    if (VALID_FIELDS.has(baseField) && VALID_OPERATORS.has(operatorName)) {
      return this.parseOperatorValue(
        baseField as TqlField,
        operatorName as StringOperator | NumericOperator | ExistenceOperator,
      );
    }

    // Check if we have field followed by dot and operator
    if (VALID_FIELDS.has(fieldName)) {
      this.consume('DOT', `Expected "." after field "${fieldName}"`);
      const opToken = this.consume('OPERATOR', 'Expected operator');
      return this.parseOperatorValue(
        fieldName as TqlField,
        opToken.value as StringOperator | NumericOperator | ExistenceOperator,
      );
    }

    throw new Error(`Unknown field: ${fieldName}`);
  }

  private parseHeaderExpression(target: 'req' | 'resp'): TqlHeaderExpression {
    // Expect ("HeaderName")
    this.consume('LPAREN', 'Expected "(" after header');
    const headerNameToken = this.consume('STRING', 'Expected header name');
    this.consume('RPAREN', 'Expected ")" after header name');

    // Then .operator(value)
    this.consume('DOT', 'Expected "." after header access');
    const opToken = this.consume('OPERATOR', 'Expected operator');
    const operator = opToken.value as StringOperator | ExistenceOperator;

    // Check if operator needs a value
    if (operator === 'exists' || operator === 'nexists') {
      this.consume('LPAREN', `Expected "()" after "${operator}"`);
      this.consume('RPAREN', `Expected "()" after "${operator}"`);
      return {
        type: 'header',
        target,
        headerName: headerNameToken.value,
        operator,
      };
    }

    // Get the value
    this.consume('LPAREN', `Expected "(" after "${operator}"`);
    const valueToken = this.advance();
    let value: string;
    if (valueToken.type === 'STRING') {
      value = valueToken.value;
    } else if (valueToken.type === 'NUMBER') {
      value = valueToken.value;
    } else {
      throw new Error(
        `Expected string or number value, got ${valueToken.type}`,
      );
    }
    this.consume('RPAREN', 'Expected ")" after value');

    return {
      type: 'header',
      target,
      headerName: headerNameToken.value,
      operator,
      value,
    };
  }

  private parseOperatorValue(
    field: TqlField,
    operator: StringOperator | NumericOperator | ExistenceOperator,
  ): TqlFieldExpression {
    // Check if operator needs a value
    if (operator === 'exists' || operator === 'nexists') {
      this.consume('LPAREN', `Expected "()" after "${operator}"`);
      this.consume('RPAREN', `Expected "()" after "${operator}"`);
      return {
        type: 'field',
        field,
        operator,
      };
    }

    // Get the value
    this.consume('LPAREN', `Expected "(" after "${operator}"`);
    const valueToken = this.advance();
    let value: string | number;

    if (valueToken.type === 'STRING') {
      value = valueToken.value;
    } else if (valueToken.type === 'NUMBER') {
      value = parseInt(valueToken.value, 10);
    } else {
      throw new Error(
        `Expected string or number value, got ${valueToken.type}`,
      );
    }
    this.consume('RPAREN', 'Expected ")" after value');

    return {
      type: 'field',
      field,
      operator,
      value,
    };
  }

  // Helper methods
  private check(type: TokenType): boolean {
    if (this.isAtEnd()) return false;
    return this.peek().type === type;
  }

  private advance(): Token {
    if (!this.isAtEnd()) this.current++;
    return this.previous();
  }

  private peek(): Token {
    return this.tokens[this.current];
  }

  private previous(): Token {
    return this.tokens[this.current - 1];
  }

  private isAtEnd(): boolean {
    return this.peek().type === 'EOF';
  }

  private consume(type: TokenType, errorMessage: string): Token {
    if (this.check(type)) return this.advance();
    throw new Error(`${errorMessage} at position ${this.peek().position}`);
  }
}

/**
 * Parse an TQL query string into an AST
 */
export function parseTql(query: string): TqlParseResult {
  const trimmed = query.trim();
  if (!trimmed) {
    return {
      success: true,
      ast: {
        type: 'field',
        field: 'req.path',
        operator: 'cont',
        value: '',
      },
    };
  }

  const cachedResult = parseResultCache.get(trimmed);
  if (cachedResult) {
    return cachedResult;
  }

  const parser = new Parser();
  const parsedResult = parser.parse(trimmed);
  if (parseResultCache.size >= PARSE_CACHE_MAX_SIZE) {
    const firstKey = parseResultCache.keys().next().value;
    if (firstKey) {
      parseResultCache.delete(firstKey);
    }
  }
  parseResultCache.set(trimmed, parsedResult);
  return parsedResult;
}

/**
 * Validate an TQL query string
 */
export function validateTql(query: string): {
  valid: boolean;
  error?: string;
} {
  const result = parseTql(query);
  if (result.success) {
    return { valid: true };
  }
  return { valid: false, error: result.error };
}
